import React, { useEffect, useRef, useState } from 'react';
import { Container } from './Dashboard.styles';
import KpiList from './KpiList/KpiList';
import TableView from './TableView';
import {
	Breadcrumbs,
	ContentWrapper,
	DateRangeWrapper,
	FilterWrapper,
} from './CategoryDashboard.styles';
import {
	filterSelectedOptions,
	transformFilterOptions,
} from '../MultisourceSkuData/MultisourceSkuData';
import MultiSelect from '../MultisourceSkuData/components/MultiSelect';
import {
	ApButtonMain,
	ApLoaderDefault,
	ApLoaderText,
} from '@alixpartners/ui-components';
import { getDatesSKU, getModuleFilter } from 'utils/apiRequests';
import { isNull } from 'lodash';
import {
	getDateRangeMonthYearSrc,
	getDateRangeMonthYearState,
} from 'CategoryManagement/utils';
import MonthPicker from 'Settings/FirstUsageSettings/MonthPicker';

const filterInitialOptions = {
	category_1: {
		options: [],
		selectedOption: [],
	},
	category_2: {
		options: [],
		selectedOption: [],
	},
	category_3: {
		options: [],
		selectedOption: [],
	},
	commodity_Code: {
		options: [],
		selectedOption: [],
	},
	commodity_Family: {
		options: [],
		selectedOption: [],
	},
	supplier_count: {
		options: [],
		selectedOption: [],
	},
	origin_Country: {
		options: [],
		selectedOption: [],
	},
	dateRange: {
		srcRange: '',
		stateRange: '',
	},
};
function createLabelValueArray() {
	const array = [];
	for (let i = 1; i <= 10; i++) {
		array.push({
			label: `> ${i}`,
			value: i,
		});
	}
	return array;
}
const CategoryDashboard = ({ modelId, filterSaved, setContent }) => {
	const [filterData, setFilterData] = useState(filterInitialOptions);
	const [isFilterLoading, setIsFilterLoading] = useState(true);
	const [skuCteFilterModel, setSkuCteFilterModel] = useState({});
	const [isRenderingInitially, setIsRenderingInitially] = useState(1);
	const gridRef = useRef();
	const filtersForTable = JSON.parse(filterSaved[0].filters);

	const checkAndFormat = data => {
		return data.filter(x => x !== null).map(x => {
			return { label: x, value: x };
		});
	};

	useEffect(() => {
		const supplierCountOptions = createLabelValueArray();
		const payload = {
			modelId: modelId,
		};
		try {
			Promise.all([getModuleFilter(payload), getDatesSKU()]).then(
				([filterResponse, dateResponse]) => {
					if (filterResponse.succeeded && dateResponse.succeeded) {
						if (!isNull(filterResponse.data)) {
							if (Object.keys(filterResponse.data).length > 0) {
								const formatedCategory1Options = checkAndFormat(
									filterResponse.data.category1
								);
								const formatedCategory2Options = checkAndFormat(
									filterResponse.data.category2
								);
								const formatedCategory3Options = checkAndFormat(
									filterResponse.data.category3
								);
								const formatedCommodityCodeOptions = checkAndFormat(
									filterResponse.data.commodityCode
								);
								const formatedcommodityFamilyOptions = checkAndFormat(
									filterResponse.data.commodityFamily
								);
								const formatedCountryOriginOptions = checkAndFormat(
									filterResponse.data.originCountry
								);
								const minDate = dateResponse?.data?.minDate.split('T')[0];
								const maxDate = dateResponse?.data?.maxDate.split('T')[0];
								const wholeRange = getDateRangeMonthYearSrc(minDate, maxDate);
								const selectedRange = getDateRangeMonthYearState(
									minDate,
									maxDate
								);
								const filterDataToSet = {
									category_1: {
										options: formatedCategory1Options,
										selectedOption: formatedCategory1Options.map(x => x?.value),
									},
									category_2: {
										options: formatedCategory2Options,
										selectedOption: formatedCategory2Options.map(x => x?.value),
									},
									category_3: {
										options: formatedCategory3Options,
										selectedOption: formatedCategory3Options.map(x => x?.value),
									},
									commodity_Code: {
										options: formatedCommodityCodeOptions,
										selectedOption: formatedCommodityCodeOptions.map(
											x => x?.value
										),
									},
									commodity_Family: {
										options: formatedcommodityFamilyOptions,
										selectedOption: formatedcommodityFamilyOptions.map(
											x => x?.value
										),
									},
									supplier_count: {
										options: supplierCountOptions,
										selectedOption: [1],
									},
									origin_Country: {
										options: formatedCountryOriginOptions,
										selectedOption: formatedCountryOriginOptions.map(
											x => x?.value
										),
									},
									dateRange: {
										srcRange: wholeRange,
										stateRange: selectedRange,
									},
								};
								setFilterData(prevState => ({
									...prevState,
									...filterDataToSet,
								}));
								setIsFilterLoading(false);

								const mergedFiltersData = transformForSkuCteFilterModel(
									filterDataToSet
								);
								setSkuCteFilterModel(mergedFiltersData);
							}
						}
					}
				}
			);
		} catch (error) {
			console.log(error);
			setIsFilterLoading(false);
		}
	}, []);

	const onFilterChangeHandler = (key, value) => {
		setFilterData(prevState => ({
			...prevState,
			[key]: {
				...prevState[key],
				selectedOption: value,
			},
		}));
	};

	function mergeFilters(obj1, obj2) {
		const merged = { ...obj1 }; // Create a shallow copy of the first object
		for (const [key, value] of Object.entries(obj2)) {
			if (merged.hasOwnProperty(key)) {
				// Key exists:
				// - If the value is an array, concatenate them (assuming unique values)
				if (Array.isArray(merged[key].values) && Array.isArray(value.values)) {
					merged[key].values = [
						...new Set([...merged[key].values, ...value.values]),
					];
				} else {
					// Handle non-array values or different structures (optional)
					// You can customize this logic based on your specific requirements
					console.warn(
						`Merging objects with different structures for key: ${key}`
					);
				}
			} else {
				// Key doesn't exist: Add it directly
				merged[key] = value;
			}
		}

		return merged;
	}

	const onFilterApplyHandler = () => {
		const mergedFiltersData = transformForSkuCteFilterModel(filterData);
		setSkuCteFilterModel(mergedFiltersData);
		if (isRenderingInitially) {
			setIsRenderingInitially(isRenderingInitially + 1);
		}
	};

	const transformForSkuCteFilterModel = filterData => {
		const filtersApplied = filterSelectedOptions(filterData);
		const filterModelData = transformFilterOptions(filtersApplied);
		const mergedFiltersData = mergeFilters(filterModelData, filtersForTable);
		return mergedFiltersData;
	};

	const Loading = () => <ApLoaderDefault loaderText={() => <ApLoaderText />} />;

	return (
		<ContentWrapper>
			{false ? (
				<div style={{ marginTop: '150px' }}>
					<Loading />
				</div>
			) : (
				<>
					<Breadcrumbs>
						<span onClick={() => setContent(0)}>Segmented Data</span>&nbsp;/&nbsp;<span
						>
							Dashboard
						</span>
					</Breadcrumbs>
					{isFilterLoading ? (
						<Loading />
					) : (
						<FilterWrapper>
							<MultiSelect
								name="category_1"
								label="Category 1"
								options={filterData.category_1.options}
								selected={filterData.category_1.selectedOption}
								isLoading={isFilterLoading}
								onChange={v => {
									onFilterChangeHandler('category_1', v);
								}}
							/>

							<MultiSelect
								name="category_2"
								label="Category 2"
								options={filterData.category_2.options}
								selected={filterData.category_2.selectedOption}
								isLoading={false}
								onChange={v => {
									onFilterChangeHandler('category_2', v);
								}}
							/>
							<MultiSelect
								name="category_3"
								label="Category 3"
								options={filterData.category_3.options}
								selected={filterData.category_3.selectedOption}
								isLoading={false}
								onChange={v => {
									onFilterChangeHandler('category_3', v);
								}}
							/>
							<MultiSelect
								name="commodity_code"
								label="Commodity Code"
								options={filterData.commodity_Code.options}
								selected={filterData.commodity_Code.selectedOption}
								isLoading={false}
								onChange={v => {
									onFilterChangeHandler('commodity_Code', v);
								}}
							/>
							<MultiSelect
								name="commodity_family"
								label="Commodity Family"
								options={filterData.commodity_Family.options}
								selected={filterData.commodity_Family.selectedOption}
								isLoading={false}
								onChange={v => {
									onFilterChangeHandler('commodity_Family', v);
								}}
							/>
							<MultiSelect
								name="supplier_count"
								label="Supplier Threshold"
								options={filterData.supplier_count.options}
								selected={filterData.supplier_count.selectedOption}
								onChange={v => {
									onFilterChangeHandler('supplier_count', v);
								}}
							/>
							<MultiSelect
								name="origin_Country"
								label="Origin Country"
								options={filterData.origin_Country.options}
								selected={filterData.origin_Country.selectedOption}
								onChange={v => {
									onFilterChangeHandler('origin_Country', v);
								}}
							/>
							<DateRangeWrapper>
								<MonthPicker
									srcRange={filterData.dateRange.srcRange}
									stateRange={filterData.dateRange.stateRange}
									onConfirm={e => {
										setFilterData(prevState => ({
											...prevState,
											dateRange: {
												...prevState.dateRange,
												stateRange: e,
											},
										}));
									}}
								/>
							</DateRangeWrapper>
							<ApButtonMain onClick={onFilterApplyHandler} small>
								Apply Filters
							</ApButtonMain>
						</FilterWrapper>
					)}

					<Container>
						{!isFilterLoading && (
							<KpiList
								modelId={modelId}
								filterData={filterData}
								counterToTriggerKPI={isRenderingInitially}
							/>
						)}
					</Container>

					{!isFilterLoading &&
						Object.keys(skuCteFilterModel).length !== 0 && (
							<TableView
								gridRef={gridRef}
								content={modelId}
								filterData={filterData}
								skuCteFilterModel={skuCteFilterModel}
								isRenderingInitially={isRenderingInitially}
							/>
						)}
				</>
			)}
		</ContentWrapper>
	);
};

export default CategoryDashboard;
